import { useEffect, useRef, useState } from 'react';
import { loadHyper } from '@juspay-tech/hyper-js';
import { HyperElements, UnifiedCheckout } from '@juspay-tech/react-hyper-js';
import { Loader2, ShieldCheck } from 'lucide-react';

const base = import.meta.env.VITE_API_URL || '/api';
type Checkout = {
  status: 'awaiting_payment' | 'paid' | 'cancelled' | 'expired';
  returnUrl?: string; orderNumber?: string; clinicName?: string;
  amountCents?: number; shippingCents?: number; networkVisitFeeCents?: number;
  items?: Array<{ name: string; quantity: number; totalCents: number }>;
  clientSecret?: string; publishableKey?: string;
};
const dollars = (cents = 0) => `$${(cents / 100).toFixed(2)}`;

/**
 * The Bloom-hosted payment page for a partner checkout. The partner's site ran
 * the intake; this page shows what the patient is buying and takes the card,
 * so card details never pass through the partner. The link in the fragment is
 * the only credential, and payment is only ever reported from the backend's
 * own reconciliation with the processor, never from a redirect parameter.
 */
export function PartnerCheckout() {
  const token = useRef(window.location.hash.slice(1));
  const [data, setData] = useState<Checkout>();
  const [hyper, setHyper] = useState<ReturnType<typeof loadHyper>>();
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const started = useRef(false);

  async function call(path: string, method = 'GET'): Promise<Checkout> {
    const response = await fetch(`${base}/partner/checkout${path}`, {
      method, headers: { Authorization: `Bearer ${token.current}` }, cache: 'no-store'
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error?.message || 'This payment link could not be opened.');
    return result.data;
  }
  async function check() {
    setChecking(true); setError('');
    try {
      const result = await call('/status');
      if (result.status === 'paid') setData(current => ({ ...current, ...result }));
      else setError('Your payment has not been confirmed yet. Finish the form, then check again.');
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to check the payment.'); }
    finally { setChecking(false); }
  }
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!/^[a-f0-9]{64}$/.test(token.current)) { setError('This payment link is invalid. Please return to the site you ordered from.'); return; }
    const meta = document.createElement('meta'); meta.name = 'referrer'; meta.content = 'no-referrer'; document.head.appendChild(meta);
    void (async () => {
      try {
        const result = await call('', 'POST');
        setData(result);
        if (result.status !== 'awaiting_payment') return;
        if (!result.publishableKey || !result.clientSecret) throw new Error('Payment is temporarily unavailable. Please try again shortly.');
        setHyper(loadHyper(result.publishableKey, {
          customBackendUrl: result.publishableKey.startsWith('pk_snd') ? 'https://sandbox.hyperswitch.io' : 'https://api.hyperswitch.io'
        }));
        // Redirect parameters are never accepted as proof of payment.
        if (window.location.search) await check();
      } catch (e) { setError(e instanceof Error ? e.message : 'Unable to open this payment.'); }
    })();
  }, []);
  // Once Bloom has confirmed the payment, hand the patient back to the partner.
  useEffect(() => {
    if (data?.status !== 'paid' || !data.returnUrl) return;
    const timer = setTimeout(() => window.location.assign(data.returnUrl!), 3000);
    return () => clearTimeout(timer);
  }, [data?.status, data?.returnUrl]);

  const checkoutOptions = { displaySavedPaymentMethodsCheckbox: false,
    sdkHandleConfirmPayment: { handleConfirm: true, buttonText: `Pay ${dollars(data?.amountCents)}`,
      confirmParams: { return_url: `${window.location.origin}/partner-checkout#${token.current}` } } };
  return <main className="sentry-block min-h-screen bg-gray-50 px-4 py-16">
    <section className="mx-auto max-w-lg rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
      <ShieldCheck className="mb-4 h-8 w-8 text-blue-600" />
      <h1 className="text-2xl font-semibold text-gray-900">{data?.clinicName ? `Pay ${data.clinicName}` : 'Complete your payment'}</h1>
      {data?.status === 'paid' ? <p role="status" className="mt-4 text-green-700">
        Thank you. Your payment is confirmed and your order {data.orderNumber} is on its way to the pharmacy.
        {data.returnUrl && <> Returning you to the site you ordered from… <a className="underline" href={data.returnUrl}>Continue now</a>.</>}
      </p> : data?.status === 'cancelled' ? <p role="alert" className="mt-4 text-sm text-red-700">This order was cancelled. Please return to the site you ordered from.</p>
      : data?.status === 'expired' ? <p role="alert" className="mt-4 text-sm text-red-700">This payment link has expired. Please return to the site you ordered from and start again.</p>
      : <>
        {data?.items && <dl className="mt-4 divide-y divide-gray-100 text-sm">
          {data.items.map((item, index) => <div key={index} className="flex justify-between py-2">
            <dt className="text-gray-700">{item.name}{item.quantity > 1 && <span className="text-gray-400"> × {item.quantity}</span>}</dt>
            <dd className="text-gray-900">{dollars(item.totalCents)}</dd>
          </div>)}
          {!!data.shippingCents && <div className="flex justify-between py-2"><dt className="text-gray-700">Shipping</dt><dd>{dollars(data.shippingCents)}</dd></div>}
          {!!data.networkVisitFeeCents && <div className="flex justify-between py-2"><dt className="text-gray-700">Provider visit fee</dt><dd>{dollars(data.networkVisitFeeCents)}</dd></div>}
          <div className="flex justify-between py-2 font-semibold"><dt>Total</dt><dd>{dollars(data.amountCents)}</dd></div>
        </dl>}
        {!!data?.networkVisitFeeCents && <p className="mt-2 text-xs text-gray-500">The provider visit fee covers the prescriber’s review and is not refundable.</p>}
        {data?.clientSecret && data.publishableKey && hyper ? <div className="mt-6">
          <HyperElements hyper={hyper} options={{ clientSecret: data.clientSecret, publishableKey: data.publishableKey, appearance: { theme: 'default' } }}>
            <UnifiedCheckout id="partner-checkout" options={checkoutOptions}
              onError={() => setError('The payment could not be completed. Check your details and try again.')} />
          </HyperElements>
          <button className="mt-4 rounded-md border border-gray-300 px-4 py-2 text-sm disabled:opacity-50" onClick={() => void check()} disabled={checking}>
            {checking ? 'Checking…' : 'Check payment status'}
          </button>
        </div> : !error && !data && <Loader2 aria-label="Loading payment" className="mt-6 h-6 w-6 animate-spin" />}
      </>}
      {error && <p role="alert" className="mt-4 text-sm text-red-700">{error}</p>}
    </section>
  </main>;
}
